import { useEffect, useState } from 'react'
import type { ENTITY_TYPES } from '@sales-center/common'
import { SOLD_SERVICE_STATUS } from '@sales-center/common'
import { useSchedulingEnabled } from './useSchedulingEnabled'
import { useAppDispatch } from '@user/store/hooks'
import { getAllEntityAppointments } from '../api'
import { unwrapResult } from '@reduxjs/toolkit'

export function useHasFreeEstimates(props: {
  entityId: number
  entityType: ENTITY_TYPES
  soldServiceStatus?: SOLD_SERVICE_STATUS
}) {
  const { entityId, entityType, soldServiceStatus } = props

  const dispatch = useAppDispatch()
  const enabled = useSchedulingEnabled()

  const [state, setState] = useState<{
    loading: boolean
    hasFreeEstimates: boolean
  }>(() => ({
    loading: enabled,
    hasFreeEstimates: false,
  }))

  useEffect(() => {
    const execute = async () => {
      try {
        const result = await dispatch(
          getAllEntityAppointments({
            entityId,
            entityType,
          }),
        )

        if (getAllEntityAppointments.fulfilled.match(result)) {
          const appts = unwrapResult(result)
          // Completed CWQ opps require a cancel reason for any scheduled
          // estimate (free or paid); otherwise only free estimates count
          const hasFreeEstimates =
            soldServiceStatus === SOLD_SERVICE_STATUS.Completed
              ? appts.some(
                  ({ appointmentType, billingType, status }) =>
                    appointmentType === 'Estimate' &&
                    status === 'Scheduled' &&
                    (billingType === 'Free' || billingType === 'Paid'),
                )
              : appts.some(
                  ({ appointmentType, billingType }) =>
                    appointmentType === 'Estimate' && billingType === 'Free',
                )

          setState({
            loading: false,
            hasFreeEstimates,
          })
        } else {
          setState({
            loading: false,
            hasFreeEstimates: false,
          })
        }
      } catch {
        setState({
          loading: false,
          hasFreeEstimates: false,
        })
      }
    }

    if (enabled) execute()
  }, [dispatch, enabled, entityId, entityType, soldServiceStatus])

  return { ...state }
}
